import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { SEO } from '../components/common';
import { ChatwootWidget, SalesCTA } from '../components/marketing';
import { openSalesChat } from '../utils/chatwootActions';
import { FiChevronDown, FiChevronUp, FiMessageSquare, FiGrid, FiUsers, FiPieChart, FiShield, FiLock, FiHeadphones, FiStar, FiCheck } from 'react-icons/fi';
import routes from '../utils/routes';

const faqs = [
  {
    question: 'How do I create an account?',
    answer: 'You can create an account by clicking the Sign Up button and entering your phone number. You will receive an OTP to verify your number, after which you can set up your profile.',
  },
  {
    question: 'How do I book an expert consultation?',
    answer: 'Navigate to the Experts section, browse through available experts, select one that matches your needs, choose a time slot, and confirm your booking. You will receive a confirmation once the expert accepts your booking.',
  },
  {
    question: 'What payment methods are accepted?',
    answer: 'We accept all major credit/debit cards, UPI payments, net banking, and wallet payments. All transactions are secured with bank-grade encryption.',
  },
  {
    question: 'How do I cancel a booking?',
    answer: 'You can cancel a booking from the My Bookings section. Please note that cancellations made less than 24 hours before the scheduled session may not be eligible for a full refund.',
  },
  {
    question: 'Are the experts SEBI registered?',
    answer: 'Yes, all our experts are SEBI registered professionals. You can verify their registration number on the SEBI website.',
  },
  {
    question: 'How do I add money to my wallet?',
    answer: 'Go to the Wallet section and click on Deposit. Enter the amount you want to add and select your preferred payment method to complete the transaction.',
  },
  {
    question: 'Is my data secure?',
    answer: 'Yes, we use industry-standard 256-bit encryption to protect your data. We never share your personal information with third parties without your consent.',
  },
  {
    question: 'How do I reset my password?',
    answer: 'Click on Forgot Password on the login screen, enter your registered phone number, and follow the instructions sent to your phone to reset your password.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Yantrai',
  url: 'https://yantrai.com',
  logo: 'https://yantrai.com/logo512.png',
  description: "India's AI-powered investment platform offering stock research, SEBI-registered expert advice, thematic investment buckets, and portfolio tracking.",
};

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const features = [
    {
      icon: FiMessageSquare,
      title: 'AI Research Copilot',
      description: 'Ask questions about stocks, companies, and market trends. Get AI-powered insights from annual reports, concalls, and news.',
    },
    {
      icon: FiGrid,
      title: 'Investment Buckets',
      description: 'Discover thematic stock groupings — from high-growth small caps to dividend champions. AI-curated and constantly updated.',
    },
    {
      icon: FiUsers,
      title: 'SEBI-Registered Experts',
      description: 'Book 1-on-1 sessions with certified financial advisors. Chat or call, get personalized investment advice.',
    },
    {
      icon: FiPieChart,
      title: 'Portfolio Tracking',
      description: 'Upload your portfolio and track real-time P&L. Monitor holdings across stocks, crypto, and gold in one place.',
    },
  ];

  const plans = [
    {
      name: 'Free',
      price: 0,
      period: 'forever',
      features: ['5 AI queries/week', '2 investment buckets', 'Basic market data'],
      highlight: false,
    },
    {
      name: 'Pro',
      price: 499,
      period: '/month',
      features: ['Unlimited AI queries', 'All 15+ buckets', 'Deep search PDFs', '10% expert discount'],
      highlight: true,
      badge: 'Most Popular',
    },
    {
      name: 'Premium',
      price: 999,
      period: '/month',
      features: ['Everything in Pro', 'Bucket price alerts', 'Priority support', '25% expert discount + 1 free'],
      highlight: false,
    },
  ];

  const testimonials = [
    { quote: 'Copilot saved me hours of research. It finds insights I would have missed!', name: 'Amit K.', location: 'Mumbai' },
    { quote: 'Found 3 multibagger stocks through Buckets. Best investment decision ever.', name: 'Priya S.', location: 'Bangalore' },
    { quote: 'Expert advice was worth every rupee. Finally confident about my portfolio.', name: 'Raj M.', location: 'Delhi' },
  ];

  const trustBadges = [
    { icon: FiShield, label: 'SEBI Registered' },
    { icon: FiLock, label: '256-bit Encryption' },
    { icon: FiHeadphones, label: '24/7 Support' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.backgroundColor, color: colors.textColor }}>
      <SEO
        title="Yantrai - AI-Powered Investment Platform | Stocks, Crypto, Gold"
        description="Yantrai is India's AI-powered investment platform. Get AI stock research, SEBI-registered expert advice, thematic investment buckets, and portfolio tracking — all in one app."
        canonical="/"
        jsonLd={[organizationJsonLd, faqJsonLd]}
      />
      <ChatwootWidget />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.gradient1 || colors.primary} 0%, ${colors.gradient2 || '#9F03F2'} 50%, ${colors.gradient1 || colors.primary} 100%)`,
        }}
      >
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center relative z-10">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl font-bold mx-auto mb-6"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', backdropFilter: 'blur(8px)' }}
          >
            Y
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            All in One Investment Platform
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Smart investing starts here. AI-powered research, SEBI experts, curated stock buckets, and portfolio tracking for Indian investors.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate(routes.signUp)}
              className="px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-200 hover:-translate-y-1 active:scale-95"
              style={{ backgroundColor: '#fff', color: colors.primary }}
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate(routes.market)}
              className="px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-200 hover:-translate-y-1 active:scale-95"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', border: '2px solid rgba(255,255,255,0.3)' }}
            >
              Explore Market
            </button>
            <SalesCTA
              context="hero"
              label="Talk to Sales"
              variant="outline"
              className="px-8 py-4 rounded-2xl font-bold text-lg"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: '2px solid rgba(255,255,255,0.3)' }}
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-3" style={{ color: colors.textColor }}>
          Everything You Need to Invest Smarter
        </h2>
        <p className="text-center mb-10" style={{ color: colors.grayScale500 }}>
          Powerful tools designed for the modern Indian investor
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="p-6 rounded-2xl transition-all duration-200 hover:-translate-y-1"
              style={{
                backgroundColor: colors.cardBackground || colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)` }}
              >
                {React.createElement(feature.icon as any, { size: 24, color: '#fff' })}
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: colors.textColor }}>
                {feature.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: colors.grayScale500 }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16" style={{ backgroundColor: colors.inputBackground }}>
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3" style={{ color: colors.textColor }}>
            Simple, Transparent Pricing
          </h2>
          <p className="text-center mb-10" style={{ color: colors.grayScale500 }}>
            Start free, upgrade when you're ready
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative p-6 rounded-2xl transition-all duration-200 hover:-translate-y-1 ${plan.highlight ? 'scale-[1.02]' : ''}`}
                style={{
                  backgroundColor: colors.cardBackground || colors.backgroundColor,
                  border: plan.highlight ? `2px solid ${colors.primary}` : `1px solid ${colors.borderColor}`,
                }}
              >
                {plan.badge && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`, color: '#fff' }}
                  >
                    {plan.badge}
                  </div>
                )}
                <h3 className="text-xl font-bold mb-2" style={{ color: colors.textColor }}>
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-extrabold" style={{ color: colors.textColor }}>
                    {plan.price === 0 ? 'Free' : `Rs.${plan.price}`}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-sm" style={{ color: colors.grayScale500 }}>{plan.period}</span>
                  )}
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm" style={{ color: colors.grayScale500 }}>
                      {React.createElement(FiCheck as any, { size: 16, color: colors.primary })}
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate(routes.pricing)}
                  className="w-full py-3 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    backgroundColor: plan.highlight ? colors.primary : 'transparent',
                    color: plan.highlight ? '#fff' : colors.primary,
                    border: plan.highlight ? 'none' : `2px solid ${colors.primary}`,
                  }}
                >
                  {plan.price === 0 ? 'Get Started' : 'Start 7-Day Free Trial'}
                </button>
                <SalesCTA
                  context={`pricing_${plan.name.toLowerCase()}` as any}
                  label="Have questions? Chat with us"
                  variant="ghost"
                  showIcon={false}
                  className="w-full justify-center mt-2 text-xs"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-3" style={{ color: colors.textColor }}>
          Trusted by 50,000+ Indian Investors
        </h2>
        <p className="text-center mb-10" style={{ color: colors.grayScale500 }}>
          See what our users are saying
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {testimonials.map((t, idx) => (
            <div
              key={idx}
              className="p-6 rounded-2xl"
              style={{
                backgroundColor: colors.cardBackground || colors.inputBackground,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              <div className="flex gap-0.5 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star}>
                    {React.createElement(FiStar as any, { size: 16, fill: '#FFB800', color: '#FFB800' })}
                  </span>
                ))}
              </div>
              <p className="text-sm mb-4 leading-relaxed" style={{ color: colors.textColor }}>
                "{t.quote}"
              </p>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ backgroundColor: colors.primary, color: '#fff' }}
                >
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: colors.textColor }}>{t.name}</div>
                  <div className="text-xs" style={{ color: colors.grayScale500 }}>{t.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          className="flex items-center justify-center gap-6 p-4 rounded-xl"
          style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
        >
          {trustBadges.map((badge, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {React.createElement(badge.icon as any, { size: 16, color: colors.grayScale500 })}
              <span className="text-xs font-medium" style={{ color: colors.grayScale500 }}>{badge.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16" style={{ backgroundColor: colors.inputBackground }}>
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: colors.textColor }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: colors.cardBackground || colors.backgroundColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <span className="text-sm font-medium pr-4" style={{ color: colors.textColor }}>
                    {faq.question}
                  </span>
                  {React.createElement(
                    expandedFaq === idx ? FiChevronUp : (FiChevronDown as any),
                    { size: 20, color: colors.grayScale500 }
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-4 pb-4" style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                    <p className="text-sm pt-3 leading-relaxed" style={{ color: colors.grayScale500 }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <p className="text-sm mb-3" style={{ color: colors.grayScale500 }}>
              Still have questions?
            </p>
            <SalesCTA context="faq" label="Chat with Us" variant="primary" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: colors.backgroundColor, borderTop: `1px solid ${colors.borderColor}` }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-sm font-bold mb-3" style={{ color: colors.textColor }}>Product</h4>
              <ul className="space-y-2">
                {[
                  { label: 'Pricing', path: routes.pricing },
                  { label: 'AI Copilot', path: routes.copilot },
                  { label: 'Buckets', path: routes.buckets },
                  { label: 'Market', path: routes.market },
                ].map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-sm hover:underline"
                      style={{ color: colors.grayScale500 }}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold mb-3" style={{ color: colors.textColor }}>Experts</h4>
              <ul className="space-y-2">
                {[
                  { label: 'Find Experts', path: routes.expertSearch },
                ].map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-sm hover:underline"
                      style={{ color: colors.grayScale500 }}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold mb-3" style={{ color: colors.textColor }}>Support</h4>
              <ul className="space-y-2">
                {[
                  { label: 'FAQ', path: routes.faqScreen },
                  { label: 'About', path: routes.aboutApp },
                ].map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-sm hover:underline"
                      style={{ color: colors.grayScale500 }}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => openSalesChat('footer')}
                    className="text-sm hover:underline"
                    style={{ color: colors.grayScale500 }}
                  >
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold mb-3" style={{ color: colors.textColor }}>Legal</h4>
              <ul className="space-y-2">
                {[
                  { label: 'Terms', path: routes.termsAndCondition },
                  { label: 'Privacy', path: routes.privacyPolicies },
                ].map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => navigate(link.path)}
                      className="text-sm hover:underline"
                      style={{ color: colors.grayScale500 }}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="pt-6 text-center" style={{ borderTop: `1px solid ${colors.borderColor}` }}>
            <p className="text-xs" style={{ color: colors.grayScale500 }}>
              &copy; {new Date().getFullYear()} Yantrai. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
